import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CACHE_EXPIRATION_TIME,
  CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
} from '@/features/constants';
import {
  SSOConfiguration,
  SSOConfigurationSchema,
} from '@/features/ssoIntegrations/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

/**
 *  Do not remove!
 *  Keep to handle not initiated SSO integrations.
 */
import mock from './mock.json';

export const ssoIntegrationsApi = createApi({
  reducerPath: 'ssoIntegrationsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/sso-configuration',
  }),
  tagTypes: ['SSOSettings', 'SSOConfigurations', 'SSOConfiguration'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getSSOSettings: builder.query<Record<string, boolean>, void>({
      query: () => ({
        url: `/settings`,
      }),
      providesTags: () => ['SSOSettings'],
      keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
    }),
    getSSOConfigurations: builder.query<
      { externalProviders: SSOConfiguration[] },
      void
    >({
      query: () => ({
        url: '/',
      }),
      transformResponse: (res: { externalProviders: SSOConfiguration[] }) => {
        const externalProviders = res?.externalProviders?.reduce(
          (acc, item) => {
            return { ...acc, [item.scheme]: item };
          },
          {} as Record<SSOConfigurationSchema, SSOConfiguration>,
        );

        return {
          externalProviders: Object.keys(SSOConfigurationSchema)
            .map(scheme => externalProviders[scheme] || mock[scheme])
            .filter(Boolean),
        };
      },
      providesTags: () => ['SSOConfigurations'],
    }),
    getSSOConfigurationBySchema: builder.query<SSOConfiguration, string>({
      query: scheme => ({
        url: `/${scheme}`,
      }),
      transformResponse: (res: SSOConfiguration, _, scheme) => {
        return res || mock[scheme];
      },
      providesTags: () => ['SSOConfiguration'],
    }),
    upsetSSOConfiguration: builder.mutation<SSOConfiguration, SSOConfiguration>(
      {
        query: data => ({
          url: `/${data.scheme}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: () => ['SSOConfigurations', 'SSOConfiguration'],
      },
    ),
    upsetPartialSSOConfiguration: builder.mutation<
      SSOConfiguration,
      { scheme: SSOConfiguration['scheme']; diff: Partial<SSOConfiguration> }
    >({
      query: ({ scheme, diff: data }) => ({
        url: `/${scheme}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: () => ['SSOConfigurations', 'SSOConfiguration'],
    }),
    toggleSSOConfiguration: builder.mutation<
      { isSuccess: boolean; errors: string[] },
      SSOConfiguration
    >({
      query: ({ scheme, isEnabled }) => ({
        url: `/${scheme}/toggle?state=${!isEnabled}`,
        method: 'POST',
      }),
      invalidatesTags: () => ['SSOConfigurations', 'SSOConfiguration'],
    }),
  }),
});

export const {
  useGetSSOSettingsQuery,
  useGetSSOConfigurationsQuery,
  useGetSSOConfigurationBySchemaQuery,
  useUpsetSSOConfigurationMutation,
  useUpsetPartialSSOConfigurationMutation,
  useToggleSSOConfigurationMutation,
} = ssoIntegrationsApi;
